import { useState, useRef, useEffect, useMemo } from "react";
import { ListFilter, Check } from "lucide-react";
import React from "react";
import { StatusInterface } from "../../../../models/TicketModule/Status";
import { useLanguageStore } from "../../../../store/useLanguageStore";

interface StatusFilterProps {
  statuses: StatusInterface[];
  selectedStatuses: string[];
  onSelectedStatusesChange: (statuses: string[]) => void;
  darkMode: boolean;
}

export function StatusFilter({ statuses, selectedStatuses, onSelectedStatusesChange, darkMode }: StatusFilterProps) {
  const { language } = useLanguageStore();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleStatus = (statusId: string) => {
    if (selectedStatuses.includes(statusId)) {
      onSelectedStatusesChange(selectedStatuses.filter((s) => s !== statusId));
    } else {
      onSelectedStatusesChange([...selectedStatuses, statusId]);
    }
  };

  const sortedStatuses = useMemo(() => [...statuses].sort((a, b) => a.order - b.order), [statuses]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`h-8 px-3 gap-2 inline-flex items-center justify-center rounded-md text-sm border transition-colors
    ${
      darkMode
        ? "bg-[#2B2C40] text-white border-white/10 hover:bg-white/10"
        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
    }
  `}
      >
        <ListFilter className="w-4 h-4" />

        {language["Statuses"]}

        <span
          className={`ml-1 px-1.5 py-0.5 rounded text-xs
      ${darkMode ? "bg-white/15 text-white" : "bg-blue-100 text-blue-700"}
    `}
        >
          {selectedStatuses.length}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg border border-gray-200 shadow-lg z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">{language["filterByStatus"]}</p>
          </div>
          <div className="py-1">
            {sortedStatuses.map((status) => {
              const isSelected = selectedStatuses.includes(status.id || "");
              const color = status.badgeColor || "gray";

              return (
                <button
                  key={status.id}
                  onClick={() => toggleStatus(status.id || "")}
                  className="w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-5 h-5 rounded border border-gray-300 flex items-center justify-center bg-white">
                    {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                  </div>
                  <div className={`w-2 h-2 rounded-full`} style={{ backgroundColor: color }}></div>
                  <span className={`text-sm ${darkMode ? "text-white" : "text-gray-700"}`}>{status.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
